import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Button, Drawer, PartialError, Tag } from '@stonelog/stonelog-design-system';
import { GingaIcon } from '@stonelog/stonelog-ginga-icons';
import {
    deleteSimulationData, postRequestPreDeliveriesAutonomos, removePreDeliveryPinsInWork,
    resetPreDeliveryPinsInWork, resetRequestPreDeliveriesStatus, resetSimulatePreDeliveriesStatus,
    routesManagementSelector, setFlagOpenTrackDeliveriesAutonomos
} from '../../../../../../../../../features/senninha/routesManagementSlice';
import Paginator from '../../../../../../../../../components/Paginator';
import UninstallationForm from '../PreDelivery/ServiceGroupForms/UninstallationForm';
import InstallationForm from '../PreDelivery/ServiceGroupForms/InstallationForm';
import MaintenanceForm from '../PreDelivery/ServiceGroupForms/MaintenanceForm';
import ExchangeForm from '../PreDelivery/ServiceGroupForms/ExchangeForm';
import EventForm from '../PreDelivery/ServiceGroupForms/EventForm';
import SupplyForm from '../PreDelivery/ServiceGroupForms/SupplyForm';
import MultiGroupForm from '../PreDelivery/ServiceGroupForms/MultiGroupForm';
import { capitalizeAllFirstLetters, toCapitalizeFirstLetterOfEachWord } from "../../../../../../../../../consumingApi/services/helper";
import machineError from "@assets/imgs/machine-error.svg";
import { setMapWorkflow } from '../../../../../../../../../features/senninha/pinsSlice';
import { usePreDeliverySimulationContext } from '../../../../../../../../../hooks/usePreDeliverySimulationContext'
import { eventSchema, exchangeSchema, installationSchema, maintenanceSchema, uninstallationSchema } from '../PreDelivery/ServiceGroupForms/schemas/schemas';
import machineSuccess from "@assets/imgs/machine-success.svg";
import speaker from '@assets/icons/speaker.svg';
import { hubsSelector } from '../../../../../../../../../features/senninha/hubsSlice';
import { getTrackingDeliveriesAutonomos } from '../../../../../../../../../features/senninha/trackingDeliveriesSlice';
import { Tooltip } from 'antd';
import { uninstallServicesTypes } from '../../../util';
import TonLogo from "@assets/icons/ton-logo.svg";
import StoneLogo from "@assets/icons/stone-logo.svg";

import * as S from '../PreDelivery/styles';

const OPERADOR_AUTONOMO = 'Autônomo';

const AutonomosSimulation = ({ onClose, onCloseWithoutModal, setActiveKeyTab }) => {

    const [activeOption, setActiveOption] = useState('list')
    const [searchedValue, setSearchedValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [filteredSimulations, setFilteredSimulations] = useState([])
    const [dataSorted, setDataSorted] = useState([])

    const dispatch = useDispatch();

    const {
        simulationsData,
        requestDeliveriesOssErrors,
        preDeliveryPinsInWork,
        statusRequestPreDeliveries,
        errorRequestPreDeliveries
    } = useSelector(routesManagementSelector);

    const {
        selectedHubInfos
    } = useSelector(hubsSelector);

    const {
        formsSimulationIdData,
        setFormsSimulationIdData,
        formsSimulationIdValitations,
        setFormsSimulationIdValidations
    } = usePreDeliverySimulationContext();

    const totalPages = simulationsData?.simulacoes?.length

    const techsData = useMemo(() => {
        const data = preDeliveryPinsInWork.reduce((res, item) => {
            if (uninstallServicesTypes.includes(item.servico)) return res;

            const contractor = item.contratante;
            if (!res[contractor]) res[contractor] = new Map()

            item.tecnologias.forEach((tech) => {
                const existing = res[contractor].get(tech)
                if (existing) {
                    existing.total += 1
                } else {
                    res[contractor].set(tech, {
                        tech,
                        total: 1,
                        icon: item.servico === 'ENVIO DE SUPRIMENTOS' ? 'coil-paper' : 'device-card-machine'
                    })
                }
            })

            return res;
        }, {})

        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [key, Array.from(value.values())])
        );
    }, [preDeliveryPinsInWork])

    const forms = {
        'INSTALAÇÃO': {
            form: InstallationForm,
            schema: installationSchema,
            notes: `Entregar 1 máquina - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'DESINSTALAÇÃO': {
            form: UninstallationForm,
            schema: uninstallationSchema,
            notes: `Coletar máquina - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'MANUTENÇÃO': {
            form: MaintenanceForm,
            schema: maintenanceSchema,
            notes: `Entregar 1 máquina e Coletar 1 máquina - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'TROCA': {
            form: ExchangeForm,
            schema: exchangeSchema,
            notes: `Entregar 1 máquina e Coletar 1 máquina - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'EVENTO': {
            form: EventForm,
            schema: installationSchema,
            notes: `Entregar 1 máquina e Coletar 1 máquina - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'ENVIO DE SUPRIMENTOS': {
            form: SupplyForm,
            schema: eventSchema,
            notes: `Entregar 1 KIT - Código do cliente: ${simulationsData?.simulacoes[currentPage - 1]?.entregas[0]?.id_atendimento}`
        },
        'MULTIGROUP': {
            form: MultiGroupForm,
            schema: null
        },
    }

    const FormRenderer = useCallback(() => {
        const servicesAmount = simulationsData?.simulacoes[currentPage - 1]?.entregas?.length
        if (!servicesAmount || servicesAmount > 1) return <></>

        const FormComponent = forms[simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.servico].form;

        const handleSaveClient = () => {
            if (currentPage === totalPages) {
                setActiveOption('list')
            } else setCurrentPage((p) => p + 1)
        }

        const isChipOs = simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.pos_recomendada.toLowerCase().includes('simcard');
        let notes = forms[simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.servico].notes;
        if (isChipOs) notes = notes.replace('máquina', 'chip')

        return (
            <FormComponent
                os={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.numero_os}
                infoOs={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.info_os}
                inputSerial={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.terminal_entrada}
                outputSerial={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.terminal_saida}
                posRecommendation={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.pos_recomendada}
                solution={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.solucao}
                defect={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.defeito}
                obsDefaultValue={notes}
                handleSaveClient={handleSaveClient}
                supplyKit={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.suprimento}
                supplyKitAmount={simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.quantidade_suprimento}
            />
        );
    }, [simulationsData, currentPage, formsSimulationIdData])

    const MultiGroupFormRenderer = useCallback(() => {
        const servicesAmount = simulationsData?.simulacoes[currentPage - 1]?.entregas?.length
        if (!servicesAmount || servicesAmount === 1) return <></>

        const FormComponent = forms['MULTIGROUP'].form;

        const handleSaveClient = () => {
            if (currentPage === totalPages) {
                setActiveOption('list')
            } else setCurrentPage((p) => p + 1)
        }

        return (
            <FormComponent
                handleSaveClient={handleSaveClient}
                attendancesData={
                    simulationsData?.simulacoes[currentPage - 1].entregas?.map((item) => ({
                        groupService: item.servico,
                        inputSerial: item.terminal_entrada,
                        outputSerial: item.terminal_saida,
                        os: item.numero_os,
                        posRecommendation: item.pos_recomendada,
                        infoOs: item.info_os,
                        solution: item.solucao,
                        defect: item.defeito,
                        supplyKit: item.suprimento,
                        supplyKitAmount: item.quantidade_suprimento,
                        isInMultiGroupForm: true,
                        id_atendimento: item.id_atendimento,
                    }))
                }
            />
        );
    }, [simulationsData, currentPage])

    const countItems = (items) => {
        const counts = items.reduce((data, item) => {
            data[item] = (data[item] || 0) + 1;
            return data;
        }, {});

        return Object.keys(counts).map((key) => ({
            name: key,
            amount: counts[key]
        }));
    }

    const getServicesFormatted = (deliveries) => {
        const aux = deliveries?.map(({ servico }) => servico)
        if (!aux) return ''

        return aux.length === 1 ?
            capitalizeAllFirstLetters(aux[0])
            : countItems(aux).map(({ name, amount }) =>
                `${capitalizeAllFirstLetters(name)} (${amount})`
            ).toString().replace(',', ', ')
    }

    const getOSsFormatted = (deliveries) => {
        const aux = deliveries?.map(({ numero_os }) => numero_os)
        if (!aux) return ''

        return aux.length === 1 ?
            aux[0]
            : aux.toString().replaceAll(',', ', ')
    }

    const handleListRowClick = (idSimulacao) => {
        const simulationIdx = simulationsData?.simulacoes.findIndex(({ id_simulacao }) => idSimulacao === id_simulacao)

        if (simulationIdx !== -1) {
            setCurrentPage(simulationIdx + 1)
            setActiveOption('client')
        }
    }

    const transformDataPerGroup = () => {
        const groupedData = {};

        Object.entries(formsSimulationIdData).forEach(([idOs, data]) => {
            const multiGroup = data.multiGroup !== null ? data.multiGroup : idOs;
            const transformedData = {
                ...data,
                formData: {
                    ...data.formData,
                    idOs,
                }
            };

            if (!groupedData[multiGroup]) {
                groupedData[multiGroup] = [];
            }

            groupedData[multiGroup].push(transformedData);
        });

        return Object.values(groupedData);
    }

    const handleRequestDeliveries = () => {
        const payload = transformDataPerGroup().map((item) => {
            const oss = item.map(({ formData, service }) => ({
                idOs: formData.idOs,
                servico: service,
                solucao: formData.solutions,
                defeito: formData.defects,
                suprimento: formData.supplyKit,
                quantidade: formData.supplyKitAmount,
                terminal_entrada: formData.inputSerial,
            }))

            return {
                external_reference_code: item[0].id_simulacao,
                poloName: selectedHubInfos.children,
                oss,
                observacao: item[0].formData.notes,
            }
        })

        dispatch(postRequestPreDeliveriesAutonomos({ payload }))
    }

    const handleDisabledAllDeliverysBtn = () => {
        const deliveries = simulationsData.simulacoes?.flatMap(({ entregas }) => entregas)

        if (!deliveries.length || !formsSimulationIdValitations || !formsSimulationIdData)
            return true

        return !deliveries.every(({ numero_os }) =>
            formsSimulationIdValitations[numero_os]?.isValid
            && (deliveries.length > 1 || formsSimulationIdData[numero_os]?.isSubmitted)
        )
    }

    const getCheckValidFormIcon = (deliveries) => {
        const isOsReady = deliveries?.every(({ numero_os }) =>
            formsSimulationIdValitations[numero_os]?.isValid
            && (deliveries?.length > 1 || formsSimulationIdData[numero_os]?.isSubmitted)
        )

        return (
            isOsReady ?
                <GingaIcon name="solid-check-round" color="#00A868" size={20} />
                :
                <Tooltip
                    title="Preenchimento pendente"
                    placement="topLeft"
                    overlayClassName="ginga-tooltip ginga-tooltip-timelime ginga-tooltip-top-right"
                >
                    <GingaIcon name="round-none" color="#A6AEBA" size={20} />
                </Tooltip>
        )
    }

    useEffect(() => {
        if (simulationsData?.simulacoes.length && searchedValue) {
            const searchedValueAux = searchedValue.toLowerCase()

            setFilteredSimulations(simulationsData.simulacoes.filter((item) =>
                item.cliente.toLowerCase().includes(searchedValueAux)
                || item.entregas?.find((item2) => item2.numero_os.toString().includes(searchedValueAux))
            ))
        }
        if (!searchedValue) setFilteredSimulations(simulationsData?.simulacoes || []);
    }, [simulationsData, searchedValue])

    useEffect(() => {
        dispatch(setMapWorkflow('routesDrawer'))
    }, [activeOption])

    useEffect(() => {
        if (simulationsData?.simulacoes?.length > 0) {
            let dataAux = {}
            let validationsAux = {}
            let dataSortedAux = []
            simulationsData.simulacoes.forEach(({ id_simulacao, reference_key, entregas }, i) => {
                dataSortedAux.push(reference_key)
                entregas?.forEach(({ numero_os, servico }) => {
                    if (!dataAux[numero_os]) {
                        dataAux = {
                            ...dataAux,
                            [numero_os]: {
                                id_simulacao,
                                reference_key,
                                formData: null,
                                service: servico,
                                isSubmitted: false,
                                multiGroup: entregas.length > 1 ? i : null
                            }
                        }
                        validationsAux = {
                            ...validationsAux,
                            [numero_os]: {
                                isValid: undefined,
                            }
                        }
                    }
                })
            })
            setFormsSimulationIdData(dataAux)
            setFormsSimulationIdValidations(validationsAux)
            setDataSorted(dataSortedAux)
        }
    }, [simulationsData?.simulacoes])

    useEffect(() => {
        if (statusRequestPreDeliveries === 'pending')
            setActiveOption('loadingRequestDeliveries')
        if (statusRequestPreDeliveries === 'fulfilled') {
            dispatch(resetRequestPreDeliveriesStatus())
            setActiveOption('successRequestDeliveries')
        }
        if (statusRequestPreDeliveries === 'rejected') {
            dispatch(resetRequestPreDeliveriesStatus())
            setActiveOption('errorRequestDeliveries')
        }
    }, [statusRequestPreDeliveries])

    const drawerHeaderContent = {
        list:
            <div style={{ height: '2.4rem' }} />,
        client:
            <Paginator
                totalPages={totalPages}
                currentPage={currentPage}
                onChange={setCurrentPage}
            />,
        loadingRequestDeliveries: null,
        successRequestDeliveries: null,
    }

    const drawerBodyContent = {
        list:
            <>
                <div style={{ margin: '0 -24px' }}>
                    <Drawer.SearchBar
                        label="Pesquisar"
                        placeholder="Nome do cliente ou Nº da OS"
                        setSearchedValue={setSearchedValue}
                        style={{ padding: '0 !important' }}
                    />
                </div>

                {!!Object.entries(techsData).length && (
                    <S.TagsContainer>
                        {Object.entries(techsData).map((item, i) => {
                            const total = item[1].reduce((res, item) => item.total + res, 0)

                            return (
                                <>
                                    {i === 1 && <S.Divider />}

                                    <div key={i}>
                                        <S.HeaderArea>
                                            {item[0].toLowerCase() === 'ton' ? <img src={TonLogo} /> : <img src={StoneLogo} />}
                                            <S.Title>{`Total de ${total} ite${total > 1 ? 'ns' : 'm'
                                                } para atendimento.`}</S.Title>
                                        </S.HeaderArea>

                                        <S.TagsArea>
                                            {item[1] && item[1]?.length ? (
                                                item[1].map((item) => (
                                                    <Tag
                                                        iconName={item.icon}
                                                        text={`${toCapitalizeFirstLetterOfEachWord(item.tech)} (${item.total
                                                            })`}
                                                    />
                                                ))
                                            ) : (
                                                <S.ResponseArea>
                                                    <S.TextResponse>Lista vazia.</S.TextResponse>
                                                </S.ResponseArea>
                                            )}
                                        </S.TagsArea>
                                    </div>
                                </>
                            );
                        })
                        }
                    </S.TagsContainer>
                )}

                <div>
                    {filteredSimulations.length && filteredSimulations.map((item) => (
                        <Drawer.Row
                            onClick={() => handleListRowClick(item.id_simulacao)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Drawer.RowText
                                text={item.cliente}
                                description={`${OPERADOR_AUTONOMO
                                    } - ${getServicesFormatted(item.entregas).toString().replaceAll(',', ', ')
                                    } (OS ${getOSsFormatted(item.entregas)})`
                                }
                            />
                            <Drawer.RowActions>
                                <Tag text="-" />
                                <Button
                                    type='negative'
                                    solid
                                    radius="99px"
                                    width="36px"
                                    height="36px"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        dispatch(deleteSimulationData(item.id_simulacao))
                                        item.entregas.forEach((item) => dispatch(removePreDeliveryPinsInWork(item.id_atendimento)))
                                    }}
                                >
                                    <GingaIcon name="trash" color="#E6171E" size={20} />
                                </Button>

                                {getCheckValidFormIcon(item.entregas)}
                            </Drawer.RowActions>
                        </Drawer.Row>
                    ))}
                </div>
            </>,
        client:
            <S.ClientArea>
                {simulationsData && simulationsData.simulacoes?.length &&
                    <>
                        <div>
                            <S.ClientInfoBlock>
                                <S.Row>
                                    <span>Polo</span>
                                    <span>{capitalizeAllFirstLetters(simulationsData?.endereco_polo)}</span>
                                </S.Row>
                            </S.ClientInfoBlock>
                            <S.ClientInfoBlock>
                                <S.Row>
                                    <span>Cliente</span>
                                    <span>{capitalizeAllFirstLetters(simulationsData?.simulacoes[currentPage - 1]?.cliente)}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Endereço</span>
                                    <span>{capitalizeAllFirstLetters(simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.endereco)}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Contratante</span>
                                    <span>{capitalizeAllFirstLetters(simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.contratante)}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Stonecode</span>
                                    <span>{simulationsData?.simulacoes[currentPage - 1]?.entregas?.[0]?.id_atendimento}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Grupo de serviço</span>
                                    <span>{getServicesFormatted(simulationsData?.simulacoes[currentPage - 1]?.entregas)}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Nº da OS</span>
                                    <span>{getOSsFormatted(simulationsData?.simulacoes[currentPage - 1]?.entregas)}</span>
                                </S.Row>
                                <S.Row>
                                    <span>Operador logístico</span>
                                    <span>{OPERADOR_AUTONOMO}</span>
                                </S.Row>
                            </S.ClientInfoBlock>
                        </div>

                        <FormRenderer />
                        <MultiGroupFormRenderer />
                    </>
                }
            </S.ClientArea>,
        loadingRequestDeliveries:
            <Drawer.Loading
                loadingMessage="Aguarde um momento, estamos solicitando as deliveries..."
            />,
        successRequestDeliveries:
            requestDeliveriesOssErrors?.length ?
                <PartialError.Root style={{
                    margin: '-24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <PartialError.Header
                        text="%b%As solicitações de deliveries autônomas foram realizadas com sucesso!%b%
                            Porém, não foi possível atualizar as OSs devido aos seguintes erros:"
                        customImg={<img src={speaker} />}
                    />
                    <PartialError.Body
                        errors={requestDeliveriesOssErrors}
                    />
                </PartialError.Root>
                :
                <Drawer.Response
                    element={<img src={machineSuccess} />}
                    message='Solicitações realizadas com sucesso!'
                />,
        errorRequestDeliveries:
            <Drawer.Response
                element={<img src={machineError} />}
                message={errorRequestPreDeliveries ? errorRequestPreDeliveries : "Houve um erro!"}
            />
    }

    const drawerFooterContent = {
        list:
            <S.BalanceArea>
                <>
                    <Button
                        type='primary'
                        width='100%'
                        height='52px'
                        size='large'
                        disabled={handleDisabledAllDeliverysBtn()}
                        onClick={handleRequestDeliveries}
                        className="ginga-lib-button"
                    >
                        Solicitar deliveries ({simulationsData?.simulacoes?.length})
                    </Button>

                    <Button
                        width='100%'
                        height='52px'
                        size='large'
                        onClick={() => {
                            onClose(() => {
                                setActiveKeyTab('1')
                                dispatch(resetPreDeliveryPinsInWork())
                                dispatch(resetSimulatePreDeliveriesStatus())
                            })
                        }}
                        className="ginga-lib-button"
                    >
                        Cancelar
                    </Button>
                </>
            </S.BalanceArea>,
        client: null,
        loadingRequestDeliveries: null,
        successRequestDeliveries:
            <>
                <Button
                    type='primary'
                    width='100%'
                    height='52px'
                    size='large'
                    onClick={() => {
                        dispatch(resetRequestPreDeliveriesStatus())
                        onCloseWithoutModal()
                        dispatch(setFlagOpenTrackDeliveriesAutonomos(true))
                        dispatch(getTrackingDeliveriesAutonomos(selectedHubInfos.children))
                    }}
                >
                    Acompanhar deliveries
                </Button>

                <Button
                    width='100%'
                    height='52px'
                    size='large'
                    onClick={() => {
                        dispatch(resetRequestPreDeliveriesStatus())
                        onClose()
                    }}
                >
                    Fechar
                </Button>
            </>,
        errorRequestDeliveries:
            <Button
                type='primary'
                width='100%'
                height='52px'
                size='large'
                onClick={handleRequestDeliveries}
            >
                Tentar novamente
            </Button>
    }

    return (
        <>
            {!!drawerHeaderContent[activeOption] &&
                <Drawer.HeaderActions
                    className="ginga-lib-header-actions"
                >
                    <S.Header>
                        <div>
                            <S.Button
                                onClick={() => setActiveOption('list')}
                                isActive={activeOption === 'list'}
                            >
                                Por lista
                            </S.Button>
                            <S.Button
                                onClick={() => setActiveOption('client')}
                                isActive={activeOption === 'client'}
                            >
                                Por cliente
                            </S.Button>
                        </div>

                        {drawerHeaderContent[activeOption]}
                    </S.Header>
                </Drawer.HeaderActions>
            }
            <Drawer.Body style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                {drawerBodyContent[activeOption]}
            </Drawer.Body>

            {!!drawerFooterContent[activeOption] &&
                <Drawer.Footer>
                    {drawerFooterContent[activeOption]}
                </Drawer.Footer>
            }
        </>
    )
};

export default AutonomosSimulation;
